import React from 'react';

import FloatingImage from './FloatingImage';

interface ImageItem {
  src: string;
  alt: string;
  style?: string;
}

interface HeroCollageProps {
  images?: ImageItem[];
  isDesktop?: boolean;
}

const CANVAS_ASPECT = 433 / 491;

const MAIN_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '0%',
  top: '0%',
  width: '45.3%',
  height: '75.6%',
};

const TOP_RIGHT_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '61.7%',
  top: '5.1%',
  width: '32.3%',
  height: '47.7%',
};

const BOTTOM_RIGHT_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '41.6%',
  top: '49.9%',
  width: '32.3%',
  height: '47.7%',
};

const DESKTOP_CANVAS_STYLE: React.CSSProperties = {
  aspectRatio: CANVAS_ASPECT,
  width:
    'var(--hero-desktop-collage-width, min(100%, clamp(320px, 30vw, 433px)))',
};

const DESKTOP_IMAGE_AREA_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset:
    '0 var(--hero-desktop-image-right-inset, clamp(20px, 1.5vw, 24px)) clamp(20px, 1.5vw, 24px) 0',
};

const getImageClassName = (image?: ImageItem): string =>
  `h-full w-full object-cover ${image?.style ?? 'object-center'}`;

const HeroCollage: React.FC<HeroCollageProps> = ({
  images = [],
  isDesktop = false,
}) => {
  if (images.length === 0) return null;

  const [main, topRight, bottomRight] = images;

  const compactCollage = (className = '') => (
    <div
      aria-hidden="true"
      className={`pointer-events-none relative h-[350px] w-[145px] shrink-0 ${className}`}
    >
      {main && (
        <FloatingImage
          path={main.src}
          alt={main.alt}
          sizes="80px"
          wrapperStyle={{
            position: 'absolute',
            right: '14px',
            top: '0px',
            width: '80.2729263305664px',
            height: '151.94517517089844px',
          }}
          offsetX={7}
          offsetY={7}
          borderRadius="72px"
          className={getImageClassName(main)}
        />
      )}

      {topRight && (
        <FloatingImage
          path={topRight.src}
          alt={topRight.alt}
          sizes="63px"
          wrapperStyle={{
            position: 'absolute',
            right: '0px',
            top: '120px',
            width: '63.112728118896484px',
            height: '105.4884033203125px',
          }}
          offsetX={6}
          offsetY={6}
          borderRadius="58px"
          className={getImageClassName(topRight)}
        />
      )}

      {bottomRight && (
        <FloatingImage
          path={bottomRight.src}
          alt={bottomRight.alt}
          sizes="54px"
          wrapperStyle={{
            position: 'absolute',
            left: '50px',
            top: '190px',
            width: '53.9744338989257px',
            height: '90.21440124511719px',
          }}
          offsetX={6}
          offsetY={6}
          borderRadius="52px"
          className={getImageClassName(bottomRight)}
        />
      )}
    </div>
  );

  const smallTabletCollage = (
    <div
      aria-hidden="true"
      className="pointer-events-none relative hidden h-[350px] w-[320px] shrink-0 min-[820px]:max-[900px]:block"
      style={{
        transform: 'scale(var(--hero-small-tablet-collage-scale, 1))',
        transformOrigin: 'top right',
      }}
    >
      {main && (
        <FloatingImage
          path={main.src}
          alt={main.alt}
          sizes="134px"
          wrapperStyle={{
            position: 'absolute',
            left: '0px',
            top: 'var(--hero-small-tablet-main-top, 0px)',
            width: '134px',
            height: '253px',
          }}
          offsetX={12}
          offsetY={12}
          borderRadius="100px"
          className={getImageClassName(main)}
        />
      )}

      {topRight && (
        <FloatingImage
          path={topRight.src}
          alt={topRight.alt}
          sizes="116px"
          wrapperStyle={{
            position: 'absolute',
            left: '146px',
            top: '20px',
            width: '116px',
            height: '194px',
          }}
          offsetX={10}
          offsetY={10}
          borderRadius="92px"
          className={getImageClassName(topRight)}
        />
      )}

      {bottomRight && (
        <FloatingImage
          path={bottomRight.src}
          alt={bottomRight.alt}
          sizes="104px"
          wrapperStyle={{
            position: 'absolute',
            left: 'var(--hero-small-tablet-third-left, 214px)',
            top: '165px',
            width: '104px',
            height: '174px',
          }}
          offsetX={9}
          offsetY={9}
          borderRadius="84px"
          className={getImageClassName(bottomRight)}
        />
      )}
    </div>
  );

  if (isDesktop) {
    return (
      <>
        {smallTabletCollage}

        <div
          aria-hidden="true"
          className="hidden shrink-0 min-[901px]:relative min-[901px]:block min-[1216px]:max-[1270px]:[--hero-desktop-collage-width:min(100%,clamp(312px,26vw,340px))] min-[1270px]:max-[1290px]:[--hero-desktop-image-right-inset:36px] lg:relative lg:block xl:w-[433px]"
          style={DESKTOP_CANVAS_STYLE}
        >
          <div style={DESKTOP_IMAGE_AREA_STYLE}>
            {main && (
              <FloatingImage
                path={main.src}
                alt={main.alt}
                sizes="(max-width: 1199px) 145px, (max-width: 1439px) 13vw, 200px"
                wrapperStyle={MAIN_STYLE}
                offsetX={20}
                offsetY={20}
                className={getImageClassName(main)}
              />
            )}

            {topRight && (
              <FloatingImage
                path={topRight.src}
                alt={topRight.alt}
                sizes="(max-width: 1199px) 103px, (max-width: 1439px) 9vw, 144px"
                wrapperStyle={TOP_RIGHT_STYLE}
                offsetX={20}
                offsetY={20}
                className={getImageClassName(topRight)}
              />
            )}

            {bottomRight && (
              <FloatingImage
                path={bottomRight.src}
                alt={bottomRight.alt}
                sizes="(max-width: 1199px) 103px, (max-width: 1439px) 9vw, 144px"
                wrapperStyle={BOTTOM_RIGHT_STYLE}
                offsetX={20}
                offsetY={20}
                className={getImageClassName(bottomRight)}
              />
            )}
          </div>
        </div>
      </>
    );
  }

  return compactCollage();
};

export default HeroCollage;
